/**
 * openExternal — open a URL outside the app WITHOUT backgrounding the app's
 * activity/task on native.
 *
 * Why: bare `Linking.openURL` on Android hands off to the external browser,
 * backgrounding our task.  Coming back used to race the token refresh while
 * Wi-Fi re-associates and could storm error toasts (see api/client.ts session
 * machinery).  An in-app browser tab (Chrome Custom Tab / SFSafariViewController
 * via react-native-inappbrowser-reborn) keeps our activity foregrounded.
 *
 * Contract:
 *  - web:    window.open(url, '_blank')
 *  - native: http(s) URLs → in-app browser tab, falling back to
 *            Linking.openURL if the in-app browser is unavailable/fails
 *  - native: NON-http(s) schemes (mailto:, tel:, market:, …) always go to
 *            Linking.openURL — the OS handler, never the in-app browser
 *  - NEVER rejects/throws to the caller; failures are logged.
 *
 * NOTE: the native module ships with the next store build (pod/gradle wiring
 * happens then); until linked, isAvailable() fails and we transparently fall
 * back to Linking.openURL — identical to the old behavior.
 */
import { Linking, Platform } from 'react-native';

// Static brand chrome for the in-app browser (can't use the useTheme hook in
// a plain util). Light-theme primary from src/utils/theme.ts.
const BROWSER_PRIMARY = '#245f73';

const HTTP_RE = /^https?:\/\//i;

export async function openExternal(url: string): Promise<void> {
  try {
    if (Platform.OS === 'web') {
      if (typeof window !== 'undefined' && window.open) {
        window.open(url, '_blank', 'noopener');
      } else {
        await Linking.openURL(url);
      }
      return;
    }

    // Non-http(s) schemes must reach the OS handler, not a browser tab.
    if (!HTTP_RE.test(url)) {
      await Linking.openURL(url);
      return;
    }

    try {
      const { default: InAppBrowser } = await import('react-native-inappbrowser-reborn');
      if (await InAppBrowser.isAvailable()) {
        await InAppBrowser.open(url, {
          // iOS (SFSafariViewController)
          dismissButtonStyle: 'done',
          preferredBarTintColor: BROWSER_PRIMARY,
          preferredControlTintColor: '#ffffff',
          animated: true,
          modalPresentationStyle: 'fullScreen',
          modalEnabled: true,
          enableBarCollapsing: true,
          // Android (Chrome Custom Tabs)
          showTitle: true,
          toolbarColor: BROWSER_PRIMARY,
          secondaryToolbarColor: '#ffffff',
          navigationBarColor: BROWSER_PRIMARY,
          enableUrlBarHiding: true,
          enableDefaultShare: true,
          showInRecents: false,
        });
        return;
      }
      await Linking.openURL(url);
    } catch {
      // In-app browser unavailable (module not linked yet, or open failed)
      // — fall back to the system browser.
      await Linking.openURL(url);
    }
  } catch (err) {
    // Never propagate: opening an external page is best-effort UX.
    console.warn('[openExternal] failed to open URL', url, err);
  }
}

export default openExternal;
